import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useAssetsConfig } from '../../../store/app/appStore';
import { buildPortalAssetUrl } from '../../utils/helpers';
import { destroy, mount, setPaused } from './catOverlay';
import type { CatSources } from './catOverlay';

/**
 * Halloween ambient effect: a cat that walks along the navigation bar. Sibling of
 * BatFlyby — same fixed, pointer-transparent overlay, same `paused` contract driven by
 * the idle manager in App.tsx. The walking itself is imperative DOM (catOverlay.ts); this
 * component owns the root element, the asset URLs and the lifecycle.
 *
 * The clips live on the assets host, so nothing mounts until `settings.assetsHost` is known
 * (`buildPortalAssetUrl` returns '' before that); the effect re-runs when it arrives.
 */

const WEBM_PATH = '/brandings/halloween/cat_walk.webm';
const HEVC_PATH = '/brandings/halloween/cat_walk.mov';
const NO_SOURCES: CatSources = { webm: '', hevc: '' };

/*
 * Stacking inside the AppContainer (`#lobby2-root` IS the AppContainer, so anything portaled
 * to `lobby2-root` lands in this same stacking context): above the navigation bar (90), the
 * status bar (100) and the search view (110) — she walks on the status bar while search is
 * open — but below the RandomGameModal overlay, which portals into `lobby2-root` at 200.
 * BatFlyby (10000) and the HalloweenIntro curtain (10001) stay above her. Overlays portaled
 * to document.body — Modal (settings), the jackpot statistics drawer — cover the whole app
 * container regardless.
 */
const CatRoot = styled.div.attrs({
  className: 'halloween-cat-root',
})`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  overflow: hidden;
  z-index: 150;
`;

interface HalloweenCatProps {
  paused?: boolean;
}

const HalloweenCat: React.FC<HalloweenCatProps> = ({ paused = false }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { assetsHost, assetsHash } = useAssetsConfig();

  // buildPortalAssetUrl derives the URLs from exactly these two settings. Empty until the host
  // is known and — like Image — until the hash has arrived on CDN hosts, which serve from a
  // hashed path (LobbyApp fetches it after the host is set).
  const sources = useMemo<CatSources>(() => {
    const waitingForHash = assetsHost.includes('cdn') && !assetsHash;
    if (!assetsHost || waitingForHash) return NO_SOURCES;
    return { webm: buildPortalAssetUrl(WEBM_PATH), hevc: buildPortalAssetUrl(HEVC_PATH) };
  }, [assetsHost, assetsHash]);
  const ready = sources.webm !== '' && sources.hevc !== '';

  useEffect(() => {
    const root = rootRef.current;
    if (!ready || !root) return undefined;
    mount(root, sources);
    return destroy;
  }, [ready, sources]);

  useEffect(() => {
    setPaused(paused);
  }, [paused]);

  return <CatRoot ref={rootRef} aria-hidden="true" />;
};

export default HalloweenCat;
